const VAULT_FORMAT = "preventivonow-vault" as const;
const ARTIFACT_FORMAT = "preventivonow-vault-artifact" as const;
const FORMAT_VERSION = 1 as const;
const AES_KEY_BYTES = 32;
const GCM_IV_BYTES = 12;
const SALT_BYTES = 16;
const RECOVERY_PREFIX = "PNV1.";

export const DEFAULT_PBKDF2_ITERATIONS = 600_000;
export const MIN_PBKDF2_ITERATIONS = 310_000;
export const MAX_PBKDF2_ITERATIONS = 2_000_000;
export const MIN_VAULT_PASSWORD_LENGTH = 12;

export type VaultArtifactKind = "pdf" | "editable_snapshot" | "search_index";

export interface AesGcmEnvelopeV1 {
  readonly algorithm: "AES-256-GCM";
  readonly iv: string;
  readonly ciphertext: string;
}

export interface PasswordKdfV1 {
  readonly algorithm: "PBKDF2";
  readonly hash: "SHA-256";
  readonly iterations: number;
  readonly salt: string;
}

export interface VaultManifestV1 {
  readonly format: typeof VAULT_FORMAT;
  readonly version: typeof FORMAT_VERSION;
  readonly vaultId: string;
  readonly createdAt: string;
  readonly passwordKdf: PasswordKdfV1;
  readonly passwordWrappedVaultKey: AesGcmEnvelopeV1;
  readonly recoveryWrappedVaultKey: AesGcmEnvelopeV1;
}

export interface VaultSession {
  readonly vaultId: string;
  readonly key: CryptoKey;
}

export interface CreatedVault {
  readonly manifest: VaultManifestV1;
  readonly recoveryCode: string;
  readonly session: VaultSession;
}

export interface EncryptedVaultArtifactV1 {
  readonly format: typeof ARTIFACT_FORMAT;
  readonly version: typeof FORMAT_VERSION;
  readonly vaultId: string;
  readonly documentId: string;
  readonly artifactKind: VaultArtifactKind;
  readonly revision: number;
  readonly mimeType: string;
  readonly createdAt: string;
  readonly plaintextByteLength: number;
  readonly encryptedDek: AesGcmEnvelopeV1;
  readonly payload: AesGcmEnvelopeV1;
  readonly ciphertextSha256: string;
}

export interface EncryptArtifactInput {
  readonly documentId?: string;
  readonly artifactKind: VaultArtifactKind;
  readonly revision: number;
  readonly mimeType: string;
  readonly plaintext: Uint8Array<ArrayBuffer>;
  readonly createdAt?: string;
}

export interface CreateVaultOptions {
  readonly iterations?: number;
  readonly createdAt?: string;
}

function requireWebCrypto(): Crypto {
  if (globalThis.crypto?.subtle === undefined) {
    throw new Error("Web Crypto non disponibile");
  }
  return globalThis.crypto;
}

function utf8(value: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(value);
}

function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(length);
  requireWebCrypto().getRandomValues(bytes);
  return bytes;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function base64UrlToBytes(value: string, label: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/u.test(value)) {
    throw new Error(`${label}: codifica base64url non valida`);
  }
  const padded = value.replaceAll("-", "+").replaceAll("_", "/")
    + "=".repeat((4 - (value.length % 4)) % 4);
  let binary: string;
  try {
    binary = atob(padded);
  } catch {
    throw new Error(`${label}: codifica base64url non valida`);
  }
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (bytesToBase64Url(bytes) !== value) {
    throw new Error(`${label}: codifica base64url non canonica`);
  }
  return bytes;
}

function assertPassword(password: string): void {
  if (password.length < MIN_VAULT_PASSWORD_LENGTH) {
    throw new Error(
      `La password del vault deve contenere almeno ${MIN_VAULT_PASSWORD_LENGTH} caratteri`,
    );
  }
}

function assertIterations(iterations: number): void {
  if (
    !Number.isSafeInteger(iterations)
    || iterations < MIN_PBKDF2_ITERATIONS
    || iterations > MAX_PBKDF2_ITERATIONS
  ) {
    throw new Error("Parametri PBKDF2 fuori dal perimetro approvabile");
  }
}

async function importAesKey(
  raw: Uint8Array<ArrayBuffer>,
  usages: readonly KeyUsage[],
  extractable = false,
): Promise<CryptoKey> {
  if (raw.byteLength !== AES_KEY_BYTES) throw new Error("Chiave AES non valida");
  return requireWebCrypto().subtle.importKey(
    "raw",
    raw,
    { name: "AES-GCM", length: 256 },
    extractable,
    [...usages],
  );
}

async function derivePasswordKek(
  password: string,
  kdf: PasswordKdfV1,
): Promise<CryptoKey> {
  assertIterations(kdf.iterations);
  const salt = base64UrlToBytes(kdf.salt, "Salt PBKDF2");
  if (salt.byteLength !== SALT_BYTES) throw new Error("Salt PBKDF2 non valido");
  const passwordKey = await requireWebCrypto().subtle.importKey(
    "raw",
    utf8(password),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return requireWebCrypto().subtle.deriveKey(
    {
      name: "PBKDF2",
      hash: kdf.hash,
      salt,
      iterations: kdf.iterations,
    },
    passwordKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

function vaultWrapAad(vaultId: string, purpose: "password" | "recovery"): Uint8Array<ArrayBuffer> {
  return utf8(`${VAULT_FORMAT}|v${FORMAT_VERSION}|${vaultId}|vault-key|${purpose}`);
}

function artifactAad(
  artifact: Pick<
    EncryptedVaultArtifactV1,
    "vaultId" | "documentId" | "artifactKind" | "revision" | "mimeType" | "plaintextByteLength"
  >,
  purpose: "dek" | "payload",
): Uint8Array<ArrayBuffer> {
  return utf8([
    ARTIFACT_FORMAT,
    `v${FORMAT_VERSION}`,
    artifact.vaultId,
    artifact.documentId,
    artifact.artifactKind,
    String(artifact.revision),
    artifact.mimeType,
    String(artifact.plaintextByteLength),
    purpose,
  ].join("|"));
}

async function aesEncrypt(
  key: CryptoKey,
  plaintext: Uint8Array<ArrayBuffer>,
  additionalData: Uint8Array<ArrayBuffer>,
): Promise<AesGcmEnvelopeV1> {
  const iv = randomBytes(GCM_IV_BYTES);
  const ciphertext = await requireWebCrypto().subtle.encrypt(
    { name: "AES-GCM", iv, additionalData, tagLength: 128 },
    key,
    plaintext,
  );
  return {
    algorithm: "AES-256-GCM",
    iv: bytesToBase64Url(iv),
    ciphertext: bytesToBase64Url(new Uint8Array(ciphertext)),
  };
}

async function aesDecrypt(
  key: CryptoKey,
  envelope: AesGcmEnvelopeV1,
  additionalData: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer>> {
  if (envelope.algorithm !== "AES-256-GCM") throw new Error("Algoritmo non supportato");
  const iv = base64UrlToBytes(envelope.iv, "Nonce AES-GCM");
  if (iv.byteLength !== GCM_IV_BYTES) throw new Error("Nonce AES-GCM non valido");
  const ciphertext = base64UrlToBytes(envelope.ciphertext, "Testo cifrato");
  if (ciphertext.byteLength < 16) throw new Error("Testo cifrato non valido");
  try {
    const plaintext = await requireWebCrypto().subtle.decrypt(
      { name: "AES-GCM", iv, additionalData, tagLength: 128 },
      key,
      ciphertext,
    );
    return new Uint8Array(plaintext);
  } catch {
    throw new Error("Decifratura rifiutata: segreto errato o contenuto manomesso");
  }
}

async function sha256Hex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  const digest = await requireWebCrypto().subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function decodeRecoveryCode(recoveryCode: string): Uint8Array<ArrayBuffer> {
  if (!recoveryCode.startsWith(RECOVERY_PREFIX)) {
    throw new Error("Codice di recupero non valido");
  }
  const bytes = base64UrlToBytes(
    recoveryCode.slice(RECOVERY_PREFIX.length),
    "Codice di recupero",
  );
  if (bytes.byteLength !== AES_KEY_BYTES) throw new Error("Codice di recupero non valido");
  return bytes;
}

function assertManifest(manifest: VaultManifestV1): void {
  if (manifest.format !== VAULT_FORMAT || manifest.version !== FORMAT_VERSION) {
    throw new Error("Formato vault non supportato");
  }
  if (!manifest.vaultId || !manifest.createdAt) throw new Error("Manifest vault incompleto");
  if (
    manifest.passwordKdf.algorithm !== "PBKDF2"
    || manifest.passwordKdf.hash !== "SHA-256"
  ) {
    throw new Error("KDF non supportata");
  }
  assertIterations(manifest.passwordKdf.iterations);
}

async function sessionFromRaw(vaultId: string, rawVaultKey: Uint8Array<ArrayBuffer>): Promise<VaultSession> {
  const key = await importAesKey(rawVaultKey, ["encrypt", "decrypt"]);
  return { vaultId, key };
}

export async function createVault(
  password: string,
  options: CreateVaultOptions = {},
): Promise<CreatedVault> {
  assertPassword(password);
  const iterations = options.iterations ?? DEFAULT_PBKDF2_ITERATIONS;
  assertIterations(iterations);
  const cryptoApi = requireWebCrypto();
  const vaultId = cryptoApi.randomUUID();
  const createdAt = options.createdAt ?? new Date().toISOString();
  const rawVaultKey = randomBytes(AES_KEY_BYTES);
  const recoveryKeyBytes = randomBytes(AES_KEY_BYTES);
  const recoveryCode = `${RECOVERY_PREFIX}${bytesToBase64Url(recoveryKeyBytes)}`;
  const passwordKdf: PasswordKdfV1 = {
    algorithm: "PBKDF2",
    hash: "SHA-256",
    iterations,
    salt: bytesToBase64Url(randomBytes(SALT_BYTES)),
  };

  try {
    const passwordKek = await derivePasswordKek(password, passwordKdf);
    const recoveryKek = await importAesKey(recoveryKeyBytes, ["encrypt", "decrypt"]);
    const [passwordWrappedVaultKey, recoveryWrappedVaultKey, session] = await Promise.all([
      aesEncrypt(passwordKek, rawVaultKey, vaultWrapAad(vaultId, "password")),
      aesEncrypt(recoveryKek, rawVaultKey, vaultWrapAad(vaultId, "recovery")),
      sessionFromRaw(vaultId, rawVaultKey),
    ]);
    return {
      manifest: {
        format: VAULT_FORMAT,
        version: FORMAT_VERSION,
        vaultId,
        createdAt,
        passwordKdf,
        passwordWrappedVaultKey,
        recoveryWrappedVaultKey,
      },
      recoveryCode,
      session,
    };
  } finally {
    rawVaultKey.fill(0);
    recoveryKeyBytes.fill(0);
  }
}

export async function unlockVaultWithPassword(
  manifest: VaultManifestV1,
  password: string,
): Promise<VaultSession> {
  assertManifest(manifest);
  const kek = await derivePasswordKek(password, manifest.passwordKdf);
  const rawVaultKey = await aesDecrypt(
    kek,
    manifest.passwordWrappedVaultKey,
    vaultWrapAad(manifest.vaultId, "password"),
  );
  try {
    return await sessionFromRaw(manifest.vaultId, rawVaultKey);
  } finally {
    rawVaultKey.fill(0);
  }
}

export async function unlockVaultWithRecoveryCode(
  manifest: VaultManifestV1,
  recoveryCode: string,
): Promise<VaultSession> {
  assertManifest(manifest);
  const recoveryKeyBytes = decodeRecoveryCode(recoveryCode);
  try {
    const kek = await importAesKey(recoveryKeyBytes, ["decrypt"]);
    const rawVaultKey = await aesDecrypt(
      kek,
      manifest.recoveryWrappedVaultKey,
      vaultWrapAad(manifest.vaultId, "recovery"),
    );
    try {
      return await sessionFromRaw(manifest.vaultId, rawVaultKey);
    } finally {
      rawVaultKey.fill(0);
    }
  } finally {
    recoveryKeyBytes.fill(0);
  }
}

export async function changeVaultPassword(
  manifest: VaultManifestV1,
  currentPassword: string,
  newPassword: string,
  iterations = DEFAULT_PBKDF2_ITERATIONS,
): Promise<VaultManifestV1> {
  assertManifest(manifest);
  assertPassword(newPassword);
  assertIterations(iterations);
  const currentKek = await derivePasswordKek(currentPassword, manifest.passwordKdf);
  const rawVaultKey = await aesDecrypt(
    currentKek,
    manifest.passwordWrappedVaultKey,
    vaultWrapAad(manifest.vaultId, "password"),
  );
  const passwordKdf: PasswordKdfV1 = {
    algorithm: "PBKDF2",
    hash: "SHA-256",
    iterations,
    salt: bytesToBase64Url(randomBytes(SALT_BYTES)),
  };
  try {
    const newKek = await derivePasswordKek(newPassword, passwordKdf);
    const passwordWrappedVaultKey = await aesEncrypt(
      newKek,
      rawVaultKey,
      vaultWrapAad(manifest.vaultId, "password"),
    );
    return { ...manifest, passwordKdf, passwordWrappedVaultKey };
  } finally {
    rawVaultKey.fill(0);
  }
}

export async function resetVaultPasswordWithRecoveryCode(
  manifest: VaultManifestV1,
  recoveryCode: string,
  newPassword: string,
  iterations = DEFAULT_PBKDF2_ITERATIONS,
): Promise<VaultManifestV1> {
  assertManifest(manifest);
  assertPassword(newPassword);
  assertIterations(iterations);
  const recoveryKeyBytes = decodeRecoveryCode(recoveryCode);
  const passwordKdf: PasswordKdfV1 = {
    algorithm: "PBKDF2",
    hash: "SHA-256",
    iterations,
    salt: bytesToBase64Url(randomBytes(SALT_BYTES)),
  };
  try {
    const recoveryKek = await importAesKey(recoveryKeyBytes, ["decrypt"]);
    const rawVaultKey = await aesDecrypt(
      recoveryKek,
      manifest.recoveryWrappedVaultKey,
      vaultWrapAad(manifest.vaultId, "recovery"),
    );
    try {
      const newPasswordKek = await derivePasswordKek(newPassword, passwordKdf);
      const passwordWrappedVaultKey = await aesEncrypt(
        newPasswordKek,
        rawVaultKey,
        vaultWrapAad(manifest.vaultId, "password"),
      );
      return { ...manifest, passwordKdf, passwordWrappedVaultKey };
    } finally {
      rawVaultKey.fill(0);
    }
  } finally {
    recoveryKeyBytes.fill(0);
  }
}

export async function encryptVaultArtifact(
  session: VaultSession,
  input: EncryptArtifactInput,
): Promise<EncryptedVaultArtifactV1> {
  if (!Number.isSafeInteger(input.revision) || input.revision < 1) {
    throw new Error("Revisione documento non valida");
  }
  if (!input.mimeType.trim()) throw new Error("MIME type mancante");
  const documentId = input.documentId ?? requireWebCrypto().randomUUID();
  const common = {
    vaultId: session.vaultId,
    documentId,
    artifactKind: input.artifactKind,
    revision: input.revision,
    mimeType: input.mimeType,
    plaintextByteLength: input.plaintext.byteLength,
  } as const;
  const rawDek = randomBytes(AES_KEY_BYTES);
  try {
    const dek = await importAesKey(rawDek, ["encrypt", "decrypt"]);
    const [encryptedDek, payload] = await Promise.all([
      aesEncrypt(session.key, rawDek, artifactAad(common, "dek")),
      aesEncrypt(dek, input.plaintext, artifactAad(common, "payload")),
    ]);
    const ciphertextBytes = base64UrlToBytes(payload.ciphertext, "Testo cifrato");
    return {
      format: ARTIFACT_FORMAT,
      version: FORMAT_VERSION,
      ...common,
      createdAt: input.createdAt ?? new Date().toISOString(),
      encryptedDek,
      payload,
      ciphertextSha256: await sha256Hex(ciphertextBytes),
    };
  } finally {
    rawDek.fill(0);
  }
}

export async function decryptVaultArtifact(
  session: VaultSession,
  artifact: EncryptedVaultArtifactV1,
): Promise<Uint8Array<ArrayBuffer>> {
  if (artifact.format !== ARTIFACT_FORMAT || artifact.version !== FORMAT_VERSION) {
    throw new Error("Formato documento cifrato non supportato");
  }
  if (artifact.vaultId !== session.vaultId) {
    throw new Error("Il documento appartiene a un vault diverso");
  }
  if (!Number.isSafeInteger(artifact.revision) || artifact.revision < 1) {
    throw new Error("Revisione documento non valida");
  }
  if (!Number.isSafeInteger(artifact.plaintextByteLength) || artifact.plaintextByteLength < 0) {
    throw new Error("Dimensione documento non valida");
  }
  const ciphertextBytes = base64UrlToBytes(artifact.payload.ciphertext, "Testo cifrato");
  if (await sha256Hex(ciphertextBytes) !== artifact.ciphertextSha256) {
    throw new Error("Checksum del documento cifrato non coincidente");
  }
  const rawDek = await aesDecrypt(
    session.key,
    artifact.encryptedDek,
    artifactAad(artifact, "dek"),
  );
  try {
    const dek = await importAesKey(rawDek, ["decrypt"]);
    const plaintext = await aesDecrypt(dek, artifact.payload, artifactAad(artifact, "payload"));
    if (plaintext.byteLength !== artifact.plaintextByteLength) {
      plaintext.fill(0);
      throw new Error("Dimensione del documento decifrato non coincidente");
    }
    return plaintext;
  } finally {
    rawDek.fill(0);
  }
}
